import { useEffect, useState } from "react";
import { CalendarDays, Check, Download, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../../i18n/LanguageContext";
import { Reveal, SectionHead } from "./Reveal";

const API = process.env.REACT_APP_BACKEND_URL;
const PUB = typeof window !== "undefined" && window.location.hostname.endsWith("margtor.africa") ? "https://margtor.africa" : API;
const LOCALE = { en: "en-GB", sw: "sw-KE", pl: "pl-PL", fr: "fr-CM", zh: "zh-CN" };
const inputCls = "w-full border border-technical bg-white px-4 py-3 text-sm text-ink placeholder:text-[#9ca3af] outline-none focus:border-red-brand transition-colors";

export const DiscoveryBooking = () => {
  const { lang, t } = useLang();
  const b = t.booking;
  const [days, setDays] = useState(null);
  const [day, setDay] = useState(null);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", topic: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const load = () =>
    fetch(`${API}/api/bookings/slots`).then((r) => r.json()).then((d) => { setDays(d.days); setDay((cur) => cur || d.days[0]?.date || null); }).catch(() => setDays([]));
  useEffect(() => { load(); }, []);

  const fmtDay = (iso) => new Date(`${iso}T12:00:00+03:00`).toLocaleDateString(LOCALE[lang], { weekday: "short", day: "numeric", month: "short" });
  const fmtTime = (iso) => new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Africa/Nairobi" });
  const fmtFull = (iso) => new Date(iso).toLocaleString(LOCALE[lang], { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit", timeZone: "Africa/Nairobi" });

  const submit = async (e) => {
    e.preventDefault();
    if (!slot) return toast.error(b.pickSlot);
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/bookings`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, slot_start: slot, lang }),
      });
      const d = await r.json();
      if (r.status === 409) { toast.error(b.taken); setSlot(null); load(); return; }
      if (!r.ok) throw new Error(d.detail || "failed");
      setDone(d);
      toast.success(b.successToast);
    } catch {
      toast.error(b.error);
    } finally {
      setBusy(false);
    }
  };

  const selected = days?.find((d) => d.date === day);

  return (
    <section id="booking" className="relative bg-ink bp-grid-dark py-20 sm:py-28 pb-28 sm:pb-28" data-testid="booking-section">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8">
        <SectionHead eyebrow={b.eyebrow} title={b.title} desc={b.desc} dark testid="booking" />
        {done ? (
          <Reveal>
            <div data-testid="booking-success" className="mt-12 max-w-2xl border border-[#f8f7f4]/15 bg-[#f8f7f4]/[0.04] p-7 sm:p-10">
              <div className="flex items-center gap-3 text-[#3ddc84]">
                <Check className="h-5 w-5" />
                <p className="font-mono-tech text-[11px] uppercase tracking-[0.2em]">{b.confirmed}</p>
              </div>
              <p className="mt-4 font-display text-2xl sm:text-3xl font-bold tracking-tight text-[#f8f7f4]">{fmtFull(done.slot_start)}</p>
              <p className="mt-1 font-mono-tech text-[11px] uppercase tracking-[0.15em] text-[#f8f7f4]/50">{b.tz}</p>
              <p className="mt-4 text-sm text-[#f8f7f4]/70">{done.emails?.visitor ? b.emailSent : b.emailPending}</p>
              <div className="mt-6 flex flex-wrap gap-2.5">
                <a href={`${PUB}/api/bookings/${done.id}.ics`} data-testid="booking-ics" className="chamfer-sm inline-flex items-center gap-2 bg-[#f8f7f4] px-4 py-2.5 font-mono-tech text-[10.5px] font-semibold uppercase tracking-[0.15em] text-ink hover:bg-[#e63946] hover:text-[#f8f7f4] transition-colors">
                  <Download className="h-4 w-4" /> {b.ics}
                </a>
                <a href={done.gcal_url} target="_blank" rel="noopener noreferrer" data-testid="booking-gcal" className="chamfer-sm inline-flex items-center gap-2 border border-[#f8f7f4]/30 px-4 py-2.5 font-mono-tech text-[10.5px] font-semibold uppercase tracking-[0.15em] text-[#f8f7f4] hover:border-[#e63946] transition-colors">
                  <ExternalLink className="h-4 w-4" /> {b.gcal}
                </a>
              </div>
            </div>
          </Reveal>
        ) : (
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
            <Reveal className="lg:col-span-7">
              <div className="border border-[#f8f7f4]/15 bg-[#f8f7f4]/[0.03] p-6 sm:p-8">
                <p className="flex items-center gap-2 font-mono-tech text-[10.5px] uppercase tracking-[0.2em] text-[#f8f7f4]/60">
                  <CalendarDays className="h-4 w-4 text-[#e63946]" /> {b.step1} · {b.tz}
                </p>
                <div className="mt-5 flex gap-2 overflow-x-auto pb-2" data-testid="booking-days">
                  {days === null && <div className="h-14 w-full animate-pulse bg-[#f8f7f4]/10" />}
                  {days?.map((d) => (
                    <button key={d.date} type="button" data-testid={`booking-day-${d.date}`} onClick={() => { setDay(d.date); setSlot(null); }}
                      className={`shrink-0 border px-4 py-2.5 font-mono-tech text-[11px] uppercase tracking-[0.12em] transition-colors ${day === d.date ? "border-[#e63946] bg-red-brand text-[#f8f7f4]" : "border-[#f8f7f4]/15 text-[#f8f7f4]/70 hover:border-[#f8f7f4]/50"}`}>
                      {fmtDay(d.date)}
                    </button>
                  ))}
                </div>
                <p className="mt-6 font-mono-tech text-[10.5px] uppercase tracking-[0.2em] text-[#f8f7f4]/60">{b.step2}</p>
                <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="booking-slots">
                  {selected?.slots.map((s) => (
                    <button key={s.start} type="button" disabled={!s.available} data-testid={`booking-slot-${fmtTime(s.start).replace(":", "")}`} onClick={() => setSlot(s.start)}
                      className={`border px-3 py-2.5 font-mono-tech text-[12px] tracking-[0.1em] transition-colors disabled:cursor-not-allowed disabled:opacity-25 disabled:line-through ${slot === s.start ? "border-[#e63946] bg-red-brand text-[#f8f7f4]" : "border-[#f8f7f4]/15 text-[#f8f7f4]/80 hover:border-[#f8f7f4]/50"}`}>
                      {fmtTime(s.start)}
                    </button>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-5">
              <form onSubmit={submit} data-testid="booking-form" className="border border-[#f8f7f4]/15 bg-[#f8f7f4] p-6 sm:p-8 space-y-3">
                <p className="font-mono-tech text-[10.5px] uppercase tracking-[0.2em] text-[#525964]">{b.step3}</p>
                <input data-testid="booking-name" required minLength={2} className={inputCls} placeholder={b.name} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <input data-testid="booking-email" required type="email" className={inputCls} placeholder={b.email} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <input data-testid="booking-company" required minLength={2} className={inputCls} placeholder={b.company} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
                <input data-testid="booking-phone" className={inputCls} placeholder={b.phone} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                <textarea data-testid="booking-topic" required minLength={2} rows={3} className={inputCls} placeholder={b.topic} value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} />
                <p data-testid="booking-selected" className="font-mono-tech text-[11px] uppercase tracking-[0.12em] text-[#525964]">
                  {slot ? `${b.selected}: ${fmtFull(slot)} EAT` : b.pickSlot}
                </p>
                <button type="submit" data-testid="booking-submit" disabled={busy} className="chamfer-sm w-full bg-red-brand px-5 py-3.5 font-mono-tech text-[11px] font-semibold uppercase tracking-[0.15em] text-[#f8f7f4] hover:bg-[#5f1019] transition-colors disabled:opacity-50">
                  {busy ? b.sending : b.submit}
                </button>
              </form>
            </Reveal>
          </div>
        )}
      </div>
    </section>
  );
};
